import type { SerializedPattern } from '../persistence/serialize';
import type { PatternWorkerRequest, PatternWorkerResponse } from './pattern-worker';

/**
 * Client du worker pipeline (Lot P3).
 *
 * File de requêtes id-ées vers un Worker unique : chaque `convert()` retourne
 * une promesse résolue quand le worker répond. Les buffers RGBA sont
 * TRANSFÉRÉS (zéro copie). `dispose()` termine le worker (onDestroy).
 *
 * Le worker est un singleton par client — le tunnel n'a besoin que d'une
 * instance ; les requêtes s'y sérialisent naturellement (le pipeline est
 * CPU-bound, le paralléliser sur N workers n'aiderait pas sur mobile).
 */

export type ConvertResult = {
  serialized: SerializedPattern;
  /** RGBA target.width × target.height (1 perle = 1 pixel). */
  preview: Uint8ClampedArray<ArrayBuffer>;
  beadsUsed: number;
  colorsUsed: number;
};

export type ConvertInput = {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  target: { width: number; height: number };
  options: PatternWorkerRequest['options'];
};

type Pending = {
  resolve: (r: ConvertResult) => void;
  reject: (e: Error) => void;
};

export class PatternWorkerClient {
  private worker: Worker | null = null;
  private disposed = false;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    this.worker = new Worker(new URL('./pattern-worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<PatternWorkerResponse>) => {
      const msg = event.data;
      const p = this.pending.get(msg.id);
      if (!p) return; // requête annulée/périmée
      this.pending.delete(msg.id);
      if (msg.ok) {
        p.resolve({
          serialized: msg.serialized,
          preview: new Uint8ClampedArray(msg.preview),
          beadsUsed: msg.beadsUsed,
          colorsUsed: msg.colorsUsed,
        });
      } else {
        p.reject(new Error(msg.error));
      }
    };
    this.worker.onerror = (e) => {
      // Panne du worker : rejeter tout ce qui est en vol.
      for (const [, p] of this.pending) {
        p.reject(new Error(`Worker pipeline en erreur : ${e.message}`));
      }
      this.pending.clear();
      // onerror = très probablement un échec de CHARGEMENT du script (chunk
      // 404 après redéploiement, offline) : le worker n'écoutera jamais rien.
      // On le tue et on le laisse respawner au prochain convert() — sinon
      // toutes les promesses suivantes ne se règlent JAMAIS (spinner infini).
      this.worker?.terminate();
      this.worker = null;
    };
    return this.worker;
  }

  convert(input: ConvertInput): Promise<ConvertResult> {
    // Client disposé (composant démonté) : refuser au lieu de ressusciter un
    // worker fantôme — 'cancelled' est déjà avalé silencieusement en amont.
    if (this.disposed) return Promise.reject(new Error('cancelled'));
    const worker = this.ensureWorker();
    const id = this.nextId++;
    // Copie du buffer source : l'appelant garde son ImageData réutilisable
    // (plusieurs tailles/styles convertis depuis le même cadrage).
    const rgbaCopy = input.rgba.slice();
    const request: PatternWorkerRequest = {
      id,
      rgba: rgbaCopy.buffer,
      width: input.width,
      height: input.height,
      target: input.target,
      options: input.options,
    };
    return new Promise<ConvertResult>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      worker.postMessage(request, [rgbaCopy.buffer]);
    });
  }

  /** Oublie les réponses encore en vol (elles seront ignorées à l'arrivée). */
  cancelAll(): void {
    for (const [, p] of this.pending) {
      p.reject(new Error('cancelled'));
    }
    this.pending.clear();
  }

  dispose(): void {
    this.disposed = true;
    this.cancelAll();
    this.worker?.terminate();
    this.worker = null;
  }
}
